import React, { useState } from "react";
import { ChevronDown, ChevronUp, ExternalLink, Image as ImageIcon } from "lucide-react";

const L = ({ href, children }) => <a href={href} target="_blank" rel="noreferrer" className="text-primary underline underline-offset-2 inline-flex items-center gap-0.5" dir="ltr">{children}<ExternalLink className="w-3 h-3" /></a>;

const Mock = ({ title, rows, button, address }) => (
  <div className="rounded-xl border border-border bg-white text-zinc-800 text-[11px] overflow-hidden shadow-inner" dir="ltr" data-testid="mail-guide-mock">
    <div className="bg-zinc-100 border-b border-zinc-200 px-3 py-1.5 font-bold text-zinc-600">{title}</div>
    <div className="p-3 space-y-2">
      {rows.map((r, i) => r.type === "check"
        ? <label key={i} className="flex items-center gap-2"><span className="w-3.5 h-3.5 rounded-sm bg-blue-600 text-white grid place-items-center text-[9px]">✓</span>{r.label}</label>
        : <div key={i}><div className="text-zinc-500 mb-0.5">{r.label}</div><div className="rounded border border-blue-500 ring-2 ring-blue-500/20 px-2 py-1 font-mono text-[11px] bg-white">{r.value || address}</div></div>)}
      <div className="pt-1"><span className="inline-block rounded bg-blue-600 text-white px-3 py-1 font-bold">{button}</span></div>
    </div>
  </div>
);

const PROVIDERS = (addr, client) => ({
  microsoft: {
    label: "Microsoft 365 / Outlook",
    note: "يُنفَّذ من حساب مدير Microsoft 365 (وليس من تطبيق Outlook في الجوال).",
    steps: [
      <>افتح <L href="https://admin.microsoft.com/#/users">admin.microsoft.com → Users → Active users</L> وسجّل دخولك بحساب المدير.</>,
      <>اضغط على المستخدم <b dir="ltr">{client}</b> ثم تبويب <b>Mail</b> ثم <b>Manage email forwarding</b>.</>,
      <>فعّل <b>Forward all emails sent to this mailbox</b>، والصق العنوان <b dir="ltr">{addr}</b>، وأبقِ <b>Keep a copy</b> مفعّلًا، ثم <b>Save changes</b>.</>,
      <>اسمح بالتوجيه الخارجي (مرة واحدة): افتح <L href="https://security.microsoft.com/antispam">security.microsoft.com → Anti-spam</L> → <b>Anti-spam outbound policy (Default)</b> → Edit protection settings → <b>Automatic forwarding rules = On – Forwarding is enabled</b> → Save. (بدونها تصلك رسالة «Undeliverable… does not allow external forwarding»).</>,
      <>انتظر 10 دقائق ثم اضغط «أنجزت التوجيه — تحقق الآن» بالأسفل.</>,
    ],
    mock: { title: "Manage email forwarding", rows: [{ type: "check", label: "Forward all emails sent to this mailbox" }, { label: "Forwarding email address *" }, { type: "check", label: "Keep a copy of forwarded email in this mailbox" }], button: "Save changes" },
  },
  google: {
    label: "Google Workspace / Gmail",
    note: "يُنفَّذ من حساب البريد نفسه عبر المتصفح.",
    steps: [
      <>افتح <L href="https://mail.google.com/mail/u/0/#settings/fwdandpop">Gmail → Settings → Forwarding and POP/IMAP</L>.</>,
      <>اضغط <b>Add a forwarding address</b> والصق <b dir="ltr">{addr}</b> ثم <b>Next → Proceed</b>.</>,
      <>سيصل رمز تأكيد إلى عنواننا — نؤكده تلقائيًا خلال دقيقة؛ ثم حدّث الصفحة.</>,
      <>اختر <b>Forward a copy of incoming mail to</b> → العنوان أعلاه → <b>keep Gmail's copy in the Inbox</b> → <b>Save Changes</b> أسفل الصفحة.</>,
      <>اضغط «أنجزت التوجيه — تحقق الآن» بالأسفل.</>,
    ],
    mock: { title: "Settings › Forwarding and POP/IMAP", rows: [{ label: "Forwarding:", value: `Forward a copy of incoming mail to ${addr}` }, { type: "check", label: "keep Gmail's copy in the Inbox" }], button: "Save Changes" },
  },
  godaddy: {
    label: "GoDaddy (Microsoft 365 من GoDaddy)",
    note: "إن اشتريت بريدك من GoDaddy فالتوجيه من لوحتها أسهل.",
    steps: [
      <>افتح <L href="https://productivity.godaddy.com/#/">productivity.godaddy.com</L> وسجّل دخول GoDaddy، ثم اضغط على البريد <b dir="ltr">{client}</b> من القائمة.</>,
      <>من قسم <b>إدارة</b> اختر <b>إعادة توجيه البريد الإلكتروني</b> (Email forwarding).</>,
      <>الصق <b dir="ltr">{addr}</b> → فعّل <b>الاحتفاظ بنسخة</b> (Keep a copy) → <b>حفظ</b>.</>,
      <>انتظر 10 دقائق ثم اضغط «أنجزت التوجيه — تحقق الآن» بالأسفل.</>,
    ],
    mock: { title: "productivity.godaddy.com › إدارة › إعادة توجيه البريد الإلكتروني", rows: [{ label: "إعادة التوجيه إلى" }, { type: "check", label: "الاحتفاظ بنسخة من البريد المُعاد توجيهه" }], button: "حفظ" },
  },
  other: {
    label: "مزوّد آخر",
    note: "Zoho، cPanel، Hostinger… كلها تملك خيار Forwarding.",
    steps: [
      <>ادخل إلى لوحة بريدك وابحث عن <b>Forwarding</b> / <b>إعادة التوجيه</b> (عادةً داخل Settings أو Email Accounts).</>,
      <>أضف العنوان <b dir="ltr">{addr}</b> واحتفظ بنسخة في صندوقك.</>,
      <>اضغط «أنجزت التوجيه — تحقق الآن» بالأسفل — أو راسلنا وسننفّذها معك.</>,
    ],
    mock: { title: "Email settings › Forwarding", rows: [{ label: "Forward to" }, { type: "check", label: "Keep a copy" }], button: "Save" },
  },
});

export const ForwardingGuide = ({ address, client }) => {
  const [prov, setProv] = useState("microsoft");
  const [more, setMore] = useState(false);
  const P = PROVIDERS(address, client)[prov];
  return (
    <div className="space-y-2" data-testid="mail-forward-guide">
      <div className="text-xs font-bold">أين بريدك حاليًا؟</div>
      <div className="flex flex-wrap gap-1.5" data-testid="mail-guide-providers">
        {Object.entries(PROVIDERS(address, client)).map(([k, v]) => (
          <button key={k} onClick={() => { setProv(k); setMore(false); }} className={`rounded-full px-3 py-1 text-[11px] font-bold border transition-colors ${prov === k ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`} data-testid={`mail-guide-${k}`}>{v.label}</button>
        ))}
      </div>
      <div className="text-[11px] text-muted-foreground">{P.note}</div>
      <ol className="space-y-1.5" data-testid="mail-forward-steps">
        {P.steps.map((s, i) => (
          <li key={i} className="flex gap-2 text-xs leading-relaxed"><span className="shrink-0 w-5 h-5 rounded-full bg-rose-500 text-white text-[10px] font-bold grid place-items-center mt-0.5">{i + 1}</span><span>{s}</span></li>
        ))}
      </ol>
      <button onClick={() => setMore((v) => !v)} className="text-[11px] font-bold text-primary flex items-center gap-1" data-testid="mail-guide-more">
        <ImageIcon className="w-3.5 h-3.5" /> {more ? "إخفاء الصورة التوضيحية" : "المزيد: شكل الشاشة المطلوبة"} {more ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </button>
      {more && <Mock {...P.mock} address={address} />}
    </div>
  );
};
